/**
 * Spaced repetition.
 *
 * The handoff's rules, with one change: "known" is earned over time, not in
 * one sitting. A word only climbs past level 1 when it is answered correctly
 * at least REVIEW_INTERVAL_S after it reached its current level — that is,
 * when the learner comes back a day later and has not forgotten it.
 *
 *   unseen        not in the map                              "new"
 *   level 0       last answer was wrong                       "learning", highest priority
 *   level 1       answered right, not yet held for a day      "learning"
 *   level 2       still right a day later                     "known"
 *   level 3       still right another day later               "known" (cap)
 *
 * Correct, from unseen or level 0: level 1, right away.
 * Correct, from level 1 or 2, a day or more later: one level up.
 * Correct, sooner than that: nothing changes — the word is not "due".
 * Wrong: level 0, whatever it was, and the word comes back 3 cards later.
 *
 * Each box carries the moment its level was reached, which is what "a day
 * later" is measured from. Time is in epoch seconds so the value can be
 * stored as-is in D1's `updated_at`.
 */
import type { Entry } from "./vocab";
import { shuffle } from "./random";

/** A word's state: level `l`, reached at epoch second `t`. */
export type Box = { l: number; t: number };
/** word -> box. Persisted per deck, per user. */
export type Boxes = Record<string, Box>;

export const MAX_LEVEL = 3;
/** How far ahead a missed word is re-inserted into the live queue. */
export const REQUEUE_GAP = 3;
/** A freshly learned word comes back this far ahead, once, to consolidate. */
const CONSOLIDATE_GAP = 10;
/** Minimum time between a level being reached and the answer that raises it. */
export const REVIEW_INTERVAL_S = 24 * 60 * 60;

export const nowSeconds = (): number => Math.floor(Date.now() / 1000);

/** Whether a correct answer now would raise this word's level. */
export function isDue(box: Box, now: number): boolean {
  return box.l >= 1 && now - box.t >= REVIEW_INTERVAL_S;
}

/**
 * The box after an answer. Returns `prev` itself — same object — when nothing
 * changes, so callers can skip persisting and re-queuing with an identity check.
 */
export function answer(prev: Box | undefined, ok: boolean, now: number): Box {
  if (!ok) return { l: 0, t: now };
  if (prev === undefined || prev.l === 0) return { l: 1, t: now };
  if (!isDue(prev, now)) return prev;
  return { l: Math.min(prev.l + 1, MAX_LEVEL), t: now };
}

/**
 * How far ahead a word comes back in this session after an answer, or null
 * to leave it to the next lap. A miss returns almost at once (the handoff's
 * rule); a word just learned comes back once to consolidate; anything else
 * waits for its review interval, which no session is long enough to cover.
 */
export function reviewGap(prev: Box | undefined, next: Box): number | null {
  if (next === prev) return null;
  if (next.l === 0) return REQUEUE_GAP;
  if (next.l === 1) return CONSOLIDATE_GAP;
  return null;
}

/**
 * Queue order: missed, then reviews that are due (lower level first), then
 * unseen, then everything not yet due — each group shuffled independently.
 * Reviews go before new words: the point of coming back is to check what has
 * been kept, and a due word left waiting behind a thousand new ones would
 * never be checked at all.
 */
export function buildQueue(entries: readonly Entry[], boxes: Boxes, now: number): Entry[] {
  const missed: Entry[] = [];
  const dueLearning: Entry[] = [];
  const dueKnown: Entry[] = [];
  const fresh: Entry[] = [];
  const learning: Entry[] = [];
  const known: Entry[] = [];

  for (const it of entries) {
    const b = boxes[it.w];
    if (b === undefined) fresh.push(it);
    else if (b.l === 0) missed.push(it);
    else if (isDue(b, now)) (b.l === 1 ? dueLearning : dueKnown).push(it);
    else (b.l === 1 ? learning : known).push(it);
  }

  return [
    ...shuffle(missed),
    ...shuffle(dueLearning),
    ...shuffle(dueKnown),
    ...shuffle(fresh),
    ...shuffle(learning),
    ...shuffle(known),
  ];
}

/** Re-insert a word `gap` positions ahead of the current one (default: a miss). */
export function requeue(
  queue: readonly Entry[],
  qpos: number,
  entry: Entry,
  gap: number = REQUEUE_GAP,
): Entry[] {
  const next = queue.slice();
  next.splice(Math.min(qpos + gap, next.length), 0, entry);
  return next;
}

export type Counts = { new: number; learning: number; known: number };

/**
 * Drives the three stat chips above the practice card.
 *
 * Counts by walking the deck rather than the box map, so a stored level for a
 * word the deck no longer contains is ignored instead of corrupting the totals.
 * That happens whenever a deck's contents change under saved progress.
 */
export function counts(entries: readonly Entry[], boxes: Boxes): Counts {
  let learning = 0;
  let known = 0;
  let seen = 0;

  for (const it of entries) {
    const box = boxes[it.w];
    if (box === undefined) continue;
    seen++;
    if (box.l >= 2) known++;
    else learning++;
  }

  return { new: entries.length - seen, learning, known };
}

/**
 * Reads one stored box, from either the current shape or the earlier plain
 * level. A bare level has no timestamp; it is stamped `now`, so a word learned
 * under the old rules becomes due a day from the upgrade rather than at once.
 * Returns null for anything else, and clamps a timestamp from the future to
 * `now` — a wrong clock must not park a word out of reach.
 */
export function readBox(raw: unknown, now: number): Box | null {
  const level = typeof raw === "number" ? raw : (raw as { l?: unknown } | null)?.l;
  if (typeof level !== "number" || !Number.isInteger(level)) return null;
  if (level < 0 || level > MAX_LEVEL) return null;

  const t = typeof raw === "number" ? now : (raw as { t?: unknown }).t;
  if (typeof t !== "number" || !Number.isInteger(t) || t < 0) return null;
  return { l: level, t: Math.min(t, now) };
}
